"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * `callback`, deferred until the calls pause for `delayMs`: each call restarts
 * the wait and the last call's arguments win — e.g. a search box that queries
 * once the person stops typing, not on every key. The wait ends on the latest
 * `callback`, so one whose identity changed while a call was pending never runs
 * stale, and the debounced function keeps its identity across renders. A call
 * still waiting when the component unmounts is dropped. For a debounced value
 * rather than a debounced call, see useDebouncedValue.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs = 500
): (...args: Args) => void {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latestCallback = useRef(callback);

  useEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return useCallback(
    (...args: Args) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(
        () => latestCallback.current(...args),
        delayMs
      );
    },
    [delayMs]
  );
}
